import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopNav from "./TopNav";
import Settings from "../settings/Settings";
import { useSettingsContext } from "../../context/SettingsContext";

interface LayoutProps {
  children: React.ReactNode;
}

const HEADER_HEIGHT = 64;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSettingsOpen, setIsSettingsOpen, setActiveSettingsTab } = useSettingsContext();

  // Redirect /settings route to home and open settings modal instead
  useEffect(() => {
    if (location.pathname === "/settings") {
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get("tab");
      
      // Set tab if present
      if (tab) {
        setActiveSettingsTab(tab);
      }
      
      // Redirect to home, preserving tab param if present
      const newPath = tab ? `/?tab=${tab}` : "/";
      navigate(newPath, { replace: true });
      
      // Open settings modal only if tab param exists
      if (tab) {
        setIsSettingsOpen(true);
      }
    }
  }, [location.pathname, navigate, setIsSettingsOpen, setActiveSettingsTab]);
  
  // Handle settings opening/closing based on route and params
  useEffect(() => {
    // First, handle /settings route redirect
    if (location.pathname === "/settings") {
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get("tab");
      
      if (tab) {
        setActiveSettingsTab(tab);
      }
      
      // Redirect to home
      const newPath = tab ? `/?tab=${tab}` : "/";
      navigate(newPath, { replace: true });
      
      // Open settings only if tab param exists
      if (tab) {
        setIsSettingsOpen(true);
      } else {
        setIsSettingsOpen(false);
      }
      return;
    }
    
    // Handle home page
    if (location.pathname === "/") {
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get("tab");
      const state = location.state as { openSettings?: boolean; settingsTab?: string } | null;
      
      // Only open settings if explicitly requested
      if (state?.openSettings) {
        if (state.settingsTab) {
          setActiveSettingsTab(state.settingsTab);
        }
        setIsSettingsOpen(true);
        // Clear state immediately to prevent re-triggering
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (tab) {
        setActiveSettingsTab(tab);
        setIsSettingsOpen(true);
        // Clean URL after opening
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // Explicitly close settings - this is the default state
        setIsSettingsOpen(false);
      }
    } else {
      // On any other route, close settings
      setIsSettingsOpen(false);
    }
  }, [location.pathname, location.search, location.state, navigate, setIsSettingsOpen, setActiveSettingsTab]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark">
      <TopNav />

      <main
        className="relative flex-grow overflow-x-hidden transition-all duration-200 bg-background-default dark:bg-background-dark flex flex-col"
        style={{
          marginTop: HEADER_HEIGHT,
          paddingBottom: '60px', // Space for fixed footer
        }}
      >
        {children}
      </main>

      {isSettingsOpen && (
        <div className="fixed top-0 left-0 w-full h-full z-[200]">
          <Settings />
        </div>
      )}
    </div>
  );
};

export default Layout;
